import { useRef } from 'react';
import { Input } from '@/shared/components/ui/input';

/**
 * Descuento cell with blur-to-commit semantics. Tracks the pre-focus value
 * locally so the parent's `onCommit` can restore it when the user cancels
 * the approval-required dialog.
 *
 * Shared between Nueva Cotización (new line editor) and the Detalle Venta
 * editor (existing line editor).
 */
export function DescuentoCell({
  descuento,
  esRegalo,
  onChange,
  onCommit,
}: {
  descuento: number;
  esRegalo: boolean;
  onChange: (next: number) => void;
  onCommit: (previous: number) => void;
}) {
  const focusValueRef = useRef(descuento);
  return (
    <div className="flex items-center gap-1">
      <Input
        // `type="text" + inputMode="numeric"` (instead of type="number")
        // because controlled number inputs can desync when the parsed
        // value is unchanged but the literal string is not — e.g.
        // typing "0" in front of "85" produces "085", which Number()
        // clamps back to 85, so React sees the state as unchanged and
        // never re-syncs the DOM display. Stripping non-digits in the
        // onChange + binding `value={String(descuento)}` keeps the
        // input pixel-perfect with the state. Matches the
        // PorcentajeTile pattern in InvoiceBreakdownCard.
        type="text"
        inputMode="numeric"
        maxLength={3}
        variant="filled"
        disabled={esRegalo}
        className="h-9 w-20 px-2 disabled:cursor-not-allowed disabled:opacity-60"
        value={String(descuento)}
        onFocus={(e) => {
          focusValueRef.current = descuento;
          e.currentTarget.select();
        }}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '');
          if (digits === '') {
            onChange(0);
            return;
          }
          const v = Number(digits);
          if (!Number.isFinite(v)) return;
          onChange(Math.min(100, Math.max(0, v)));
        }}
        onBlur={() => onCommit(focusValueRef.current)}
      />
      <span className="text-xs text-muted-foreground">%</span>
    </div>
  );
}
